import { useState, useRef, useEffect } from "react";
import { X, Users, Clock, ChevronDown, Info, CalendarCheck, Loader2, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { createUserReservations } from "../../../lib/api/public.reservations.apis";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ReservationPopupProps {
  restaurantId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

interface TimeSlot {
  label: string;
  dayLabel: string;
  timeLabel: string;
  startTime: string;
  endTime: string;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const OCCASIONS = [
  { id: "birthday",    label: "Birthday" },
  { id: "anniversary", label: "Anniversary" },
  { id: "date",        label: "Date Night" },
  { id: "business",   label: "Business" },
  { id: "special",    label: "Special" },
  { id: "meal",       label: "Casual Meal" },
];

const PARTY_SIZES = [1, 2, 3, 4, 5, 6];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function generateTimeSlots(): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const now = new Date();
  now.setMinutes(now.getMinutes() < 30 ? 30 : 60, 0, 0);

  for (let day = 0; day < 7; day++) {
    const base = new Date(now);
    base.setDate(base.getDate() + day);
    base.setHours(11, 0, 0, 0);

    for (let h = 11; h <= 21; h++) {
      for (const m of [0, 30]) {
        const start = new Date(base);
        start.setHours(h, m, 0, 0);
        if (start <= now) continue;

        const end = new Date(start);
        end.setHours(end.getHours() + 1, end.getMinutes() + 30);

        const isToday = start.toDateString() === new Date().toDateString();
        const isTomorrow =
          start.toDateString() ===
          new Date(Date.now() + 86400000).toDateString();

        const dayLabel = isToday
          ? "Today"
          : isTomorrow
          ? "Tomorrow"
          : start.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            });

        const timeLabel = start.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
        });

        slots.push({
          label: `${dayLabel} · ${timeLabel}`,
          dayLabel,
          timeLabel,
          startTime: start.toISOString(),
          endTime: end.toISOString(),
        });
      }
    }
  }
  return slots.slice(0, 48);
}

// Group slots by day
function groupByDay(slots: TimeSlot[]): Record<string, TimeSlot[]> {
  return slots.reduce<Record<string, TimeSlot[]>>((acc, slot) => {
    if (!acc[slot.dayLabel]) acc[slot.dayLabel] = [];
    acc[slot.dayLabel].push(slot);
    return acc;
  }, {});
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2.5">
    {children}
  </p>
);

const FieldCard = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">{children}</div>
);

// ─── Component ─────────────────────────────────────────────────────────────────

export default function ReservationPopup({
  restaurantId,
  onClose,
  onSuccess,
}: ReservationPopupProps) {
  const [step, setStep] = useState<"form" | "success">("form");
  const [reservationId, setReservationId] = useState<string | null>(null);

  // Form state
  const [partySize, setPartySize]           = useState(2);
  const [selectedSlot, setSelectedSlot]     = useState<TimeSlot | null>(null);
  const [occasion, setOccasion]             = useState<string | null>(null);
  const [specialRequest, setSpecialRequest] = useState("");
  const [phone, setPhone]                   = useState("");
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [showTooltip, setShowTooltip]       = useState(false);
  const [activeDayIndex, setActiveDayIndex] = useState(0);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const timeSlots   = generateTimeSlots();
  const grouped     = groupByDay(timeSlots);
  const days        = Object.keys(grouped);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const overlayRef  = useRef<HTMLDivElement>(null);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setShowTimeDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isValid = phone.trim().length >= 7 && selectedSlot !== null;

  async function handleSubmit() {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await createUserReservations(restaurantId, {
          phone: phone.trim(),
          partySize,
          startTime: selectedSlot!.startTime,
          endTime: selectedSlot!.endTime,
          occasion,
          source: "gemio_app"
      });

      setReservationId(data._id ?? data.reservationId ?? null);
      setStep("success");
      onSuccess?.();
    } catch (err: any) {
      console.error("Reservation creation failed:", err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-[2px]"
    >
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        style={{ maxHeight: "92dvh" }}
      >
        {/* Top drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Reserve a Table</h2>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the details below to book</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div
          className="overflow-y-auto px-6 pb-8 pt-5 space-y-5"
          style={{ maxHeight: "calc(92dvh - 72px)" }}
        >
          <AnimatePresence mode="wait">

            {/* ── FORM ─────────────────────────────────────────────────────── */}
            {step === "form" && (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.18 }}
                className="space-y-5"
              >

                {/* Party size */}
                <FieldCard>
                  <Label>
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="w-3 h-3" /> Party Size
                    </span>
                  </Label>
                  <div className="flex gap-2 flex-wrap">
                    {PARTY_SIZES.map((n) => (
                      <button
                        key={n}
                        onClick={() => setPartySize(n)}
                        className={`w-10 h-10 rounded-xl text-sm font-semibold border transition-all duration-150
                          ${partySize === n
                            ? "bg-gray-900 border-gray-900 text-white shadow-sm"
                            : "bg-white border-gray-200 text-gray-600 hover:border-gray-400"
                          }`}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      onClick={() => setPartySize(9)}
                      className={`px-4 h-10 rounded-xl text-sm font-semibold border transition-all duration-150
                        ${partySize >= 9
                          ? "bg-gray-900 border-gray-900 text-white shadow-sm"
                          : "bg-white border-gray-200 text-gray-600 hover:border-gray-400"
                        }`}
                    >
                      7+
                    </button>
                  </div>
                </FieldCard>

                {/* Date & time picker */}
                <FieldCard>
                  <Label>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> Date & Time
                    </span>
                  </Label>

                  {/* Day tabs */}
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => setActiveDayIndex((i) => Math.max(0, i - 1))}
                      disabled={activeDayIndex === 0}
                      className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 disabled:opacity-30 hover:bg-gray-100 transition-colors shrink-0"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex gap-1.5 overflow-hidden flex-1">
                      {days.slice(activeDayIndex, activeDayIndex + 3).map((day) => {
                        const isActive = selectedSlot?.dayLabel === day;
                        return (
                          <button
                            key={day}
                            onClick={() => {
                              const first = grouped[day][0];
                              setSelectedSlot(first);
                            }}
                            className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all truncate
                              ${isActive
                                ? "bg-gray-900 border-gray-900 text-white"
                                : "bg-white border-gray-200 text-gray-600 hover:border-gray-400"
                              }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setActiveDayIndex((i) => Math.min(days.length - 3, i + 1))}
                      disabled={activeDayIndex >= days.length - 3}
                      className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 disabled:opacity-30 hover:bg-gray-100 transition-colors shrink-0"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Time slots for selected day */}
                  {selectedSlot?.dayLabel && (
                    <div className="flex flex-wrap gap-1.5">
                      {(grouped[selectedSlot.dayLabel] ?? grouped[days[0]]).map((slot) => {
                        const active = selectedSlot?.startTime === slot.startTime;
                        return (
                          <button
                            key={slot.startTime}
                            onClick={() => setSelectedSlot(slot)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150
                              ${active
                                ? "bg-gray-900 border-gray-900 text-white"
                                : "bg-white border-gray-200 text-gray-600 hover:border-gray-400"
                              }`}
                          >
                            {slot.timeLabel}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Fallback – no day selected yet */}
                  {!selectedSlot && (
                    <div className="flex flex-wrap gap-1.5">
                      {(grouped[days[0]] ?? []).map((slot) => (
                        <button
                          key={slot.startTime}
                          onClick={() => setSelectedSlot(slot)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:border-gray-400 bg-white transition-all"
                        >
                          {slot.timeLabel}
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedSlot && (
                    <p className="mt-2 text-xs text-gray-400">
                      Selected: <span className="font-medium text-gray-700">{selectedSlot.label}</span>
                    </p>
                  )}
                </FieldCard>

                {/* Occasion */}
                <FieldCard>
                  <Label>
                    Occasion{" "}
                    <span className="normal-case font-normal tracking-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {OCCASIONS.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => setOccasion(occasion === o.id ? null : o.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150
                          ${occasion === o.id
                            ? "bg-gray-900 border-gray-900 text-white"
                            : "bg-white border-gray-200 text-gray-600 hover:border-gray-400"
                          }`}
                      >
                        
                        {o.label}
                      </button>
                    ))}
                  </div>
                </FieldCard>

                {/* Special requests */}
                <div>
                  <Label>
                    Special Requests{" "}
                    <span className="normal-case font-normal tracking-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <textarea
                    value={specialRequest}
                    onChange={(e) => setSpecialRequest(e.target.value)}
                    placeholder="e.g. Window seat, high chair, allergy info…"
                    rows={3}
                    className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-gray-50 focus:border-gray-400 focus:bg-white focus:outline-none resize-none placeholder-gray-400 text-gray-800 transition-all"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label>
                    <span className="inline-flex items-center gap-1.5">
                      Your Phone Number
                      <div className="relative inline-flex">
                        <button
                          onMouseEnter={() => setShowTooltip(true)}
                          onMouseLeave={() => setShowTooltip(false)}
                          className="text-gray-300 hover:text-gray-500 transition-colors"
                          aria-label="Why we need your phone number"
                        >
                          <Info className="w-3 h-3" />
                        </button>
                        <AnimatePresence>
                          {showTooltip && (
                            <motion.div
                              initial={{ opacity: 0, y: 2 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 2 }}
                              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 bg-gray-900 text-white text-[11px] rounded-xl px-3 py-2 text-center leading-relaxed z-30 shadow-lg pointer-events-none"
                            >
                              Used only to confirm your booking.
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </span>
                  </Label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+254 7XX XXX XXX"
                    className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-gray-50 focus:border-gray-400 focus:bg-white focus:outline-none placeholder-gray-400 text-gray-800 transition-all"
                  />
                </div>

                {/* Error banner */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
                    >
                      {error}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Submit CTA */}
                <div className="space-y-3 pt-1">
                  <button
                    onClick={handleSubmit}
                    disabled={!isValid || submitting}
                    className={`w-full py-3.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2
                      ${isValid && !submitting
                        ? "bg-gray-900 hover:bg-gray-700 text-white shadow-sm active:scale-[0.98]"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      }`}
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Reserving…</>
                    ) : (
                      <><CalendarCheck className="w-4 h-4" /> Confirm Reservation</>
                    )}
                  </button>

                  <p className="text-center text-[11px] text-gray-400 leading-relaxed">
                    By reserving you agree to Gemio's{" "}
                    <a href="/about-gemio#privacy" target="_blank" className="underline hover:text-gray-600 transition-colors">
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a href="/terms" target="_blank" className="underline hover:text-gray-600 transition-colors">
                      Terms of Use
                    </a>.
                  </p>
                </div>
              </motion.div>
            )}

            {/* ── SUCCESS ───────────────────────────────────────────────────── */}
            {step === "success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.22 }}
                className="flex flex-col items-center text-center py-8 space-y-4"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 }}
                  className="w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center shadow-md"
                >
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </motion.div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">You're all set!</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Table reserved for{" "}
                    <span className="font-semibold text-gray-800">{selectedSlot?.label}</span>
                    {" · "}
                    <span className="font-semibold text-gray-800">
                      {partySize} {partySize === 1 ? "guest" : "guests"}
                    </span>
                  </p>
                  {occasion && (
                    <p className="text-xs text-gray-400 mt-1">
                      {OCCASIONS.find((o) => o.id === occasion)?.label}
                    </p>
                  )}
                </div>

                <div className="w-full rounded-2xl border border-gray-100 bg-gray-50 px-5 py-4 space-y-2 text-sm text-left">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Confirmation to</span>
                    <span className="font-medium text-gray-800">{phone}</span>
                  </div>
                  {reservationId && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Booking ID</span>
                      <span className="font-mono text-xs text-gray-600">{reservationId}</span>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-400">
                  You'll receive a confirmation shortly. See you there!
                </p>

                <button
                  onClick={onClose}
                  className="w-full py-3.5 rounded-xl bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold transition-colors active:scale-[0.98]"
                >
                  Done
                </button>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}